import native from 'src/config/native.js'

export default function registerSduiDirectives(app) {
  const triggerHapticsImpact = () => native.hapticsImpact()
  app.directive('haptic-feedback', {
    beforeMount: (el) => {
      el.addEventListener('click', triggerHapticsImpact)
    },
    unmounted: (el) => {
      el.removeEventListener('click', triggerHapticsImpact)
    }
  })

  /**
   * Custom directive for line-clamp behavior
   * Example usage: v-line-clamp="{ maxLines: 3, (Optional, default: '...') ellipsis: 'read more'}"
   * Ideally we would do this with CSS "-webkit-line-clamp" but this has some issues:
   * - Only works on single <p>
   * - No RTL support
   * - Works differently in safari than in chrome
   * Downside of this directive is that it always adds a new <span> for the ellipsis which may look like a new line.
   */
  app.directive('line-clamp', {
    mounted: (el, binding) => {
      if (!el.textContent || !binding.value?.maxLines) return

      // detect the elements line-height
      const style = window.getComputedStyle(el)
      if (!style) return

      const lineHeightPx = style.getPropertyValue('line-height')
      if (!lineHeightPx) return

      const ellipsis = binding.value?.ellipsis || '...'

      // Determining the maximumHeight by multiplying the lineHeight with the maximum number of lines
      const maximumHeight = (Number(binding.value.maxLines) * Number(lineHeightPx.replace('px', '')))

      // don't display ellipsis if the element is smaller than the maximumHeight
      if (el.offsetHeight <= maximumHeight) return

      // set the height to the element
      el.style.height = `${maximumHeight}px`

      // append the ellipsis
      const newElement = document.createElement('span')
      newElement.innerHTML = ellipsis
      el.parentNode.insertBefore(newElement, el.nextSibling)
    }
  })

  /**
   * Custom directive for text-truncate behaviour if we need to truncate depending on parentElement and having a
   * single-dot-ellipsis at the end.
   * This methods checks if the textElement has a bigger width than the parent element and if so it clears the textElement,
   * loops through the original text and adds them until max content size is reached. It then adds the ellipsis at the end.
   * Example usage: v-text-truncate-to-parents
   *
   * BEWARE: This directive won't work if there is any overflow property set on the textElement
   * TODO JHE: Implement addEventListener logic to react to windowSize changes without having listener from outside
   */
  app.directive('text-truncate-to-parent', {
    mounted: (el) => {
      if (!el?.textContent || !el?.offsetWidth) return

      // get the width of the textElement and its parentElement
      const elementWidth = el.offsetWidth
      const parentElementWidth = el.parentElement?.offsetWidth

      // textElement is already smaller than parent, nothing to truncate -> return
      if (!elementWidth || !parentElementWidth || (elementWidth <= parentElementWidth)) return

      const originalTextContent = el.textContent

      // subtracting 3 pixels from maxWidth so we have enough space for adding the dot-ellipsis
      const maxTextElementWidth = Number(parentElementWidth) - 3

      // clear the textContent
      el.textContent = ''

      const spreadTextContent = [...originalTextContent]
      spreadTextContent.some((currChar) => {
        // add the chars one by one until maxWidth is reached
        el.textContent += currChar
        return el.offsetWidth >= maxTextElementWidth
      })

      // Because not every char has the same width, we need to check if element width is now bigger than maxWidth
      // if that's the case we remove the last char again. In this case the loop will break out.
      // We could avoid this if we calculate the width of every char depending on the font-size, weight, etc. but this
      // is way too complex. Instead we can just set the textContent and call offsetWidth, let the browser do the calculation.
      if (el.offsetWidth > maxTextElementWidth) el.textContent = el.textContent.slice(0, -1)

      // finally, attach the dot-ellipsis at the end of the newly created textContent
      el.textContent += '.'
    }
  })

  /**
   * Custom directive to restore focus to a button after it has been disabled.
   * We often disable buttons to prevent multiple, parallel API calls.
   * Disabling an element causes it to lose focus, which is confusing for users who rely on keyboard or screen readers.
   * This directive watches the button's disabled attribute and restores focus when it is removed/set to false.
   */
  app.directive('btn-focus-restore-after-disable', {
    beforeMount (el) {
      el.__focusOutHandler__ = function () {
        const observer = new MutationObserver(() => {
          if (!el.disabled) {
            el.focus()
            observer.disconnect()
          }
        })
        observer.observe(el, { attributes: true, attributeFilter: ['disabled'] })

        el._disabledObserver = observer
      }
      el.addEventListener('focusout', el.__focusOutHandler__)
    },

    unmounted (el) {
      if (el._disabledObserver) {
        el._disabledObserver.disconnect()
        delete el._disabledObserver
      }
    }
  })
}
